import { ApiError, hasCode } from '@fcms/ui'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api, installCredentials } from '@/api/client'
import type {
  LoginResult,
  StaffSessionInfo,
  StaffUser,
  TotpEnrolment,
} from '@/api/undocumented'
import type { Permission } from '@/auth/permissions'

/**
 * Where the session token lives, and why.
 *
 * `sessionStorage`, not `localStorage`: a back-office session is per tab and
 * ends with the tab. Staff share machines at a collection point, and a token
 * that outlives the browser window is a token the next person inherits.
 * `localStorage` would also make "sign out on this device" a lie in every
 * other tab.
 *
 * The token is opaque and the server stores only its SHA-256, so what is kept
 * here is a bearer credential and nothing else — no claims, no permissions.
 * Permissions come from `/auth/me` on every load, which is what lets a role
 * change take effect without forcing a sign-out.
 */
const TOKEN_KEY = 'fcms.backoffice.token'

function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function writeStoredToken(token: string | null): void {
  try {
    if (token === null) sessionStorage.removeItem(TOKEN_KEY)
    else sessionStorage.setItem(TOKEN_KEY, token)
  } catch {
    // A locked-down profile refuses storage. The session still works for this
    // page load; it simply does not survive a reload. Failing the sign-in over
    // it would be worse.
  }
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(readStoredToken())
  const user = ref<StaffUser | null>(null)

  /** True until the first `/auth/me` has resolved one way or the other. */
  const resolving = ref(false)
  const ready = ref(false)

  /** Set when the password step succeeded and a TOTP code is now required. */
  const totpChallenge = ref<string | null>(null)

  const signedIn = computed(() => token.value !== null && user.value !== null)

  const permissions = computed(() => new Set(user.value?.permissions ?? []))

  /**
   * Site scope. An empty or absent list means unrestricted — the same
   * distinction `auth.Principal.SiteScope` carries server-side, where nil is
   * "every site" and it is never collapsed into "no sites".
   */
  const siteScope = computed<string[]>(() => user.value?.site_scope ?? [])
  const siteRestricted = computed(() => siteScope.value.length > 0)

  function can(permission: Permission | string): boolean {
    return permissions.value.has(permission)
  }

  function canAny(...wanted: (Permission | string)[]): boolean {
    return wanted.some((p) => permissions.value.has(p))
  }

  function setSession(nextToken: string, nextUser: StaffUser): void {
    token.value = nextToken
    user.value = nextUser
    totpChallenge.value = null
    writeStoredToken(nextToken)
  }

  function clearSession(): void {
    token.value = null
    user.value = null
    totpChallenge.value = null
    writeStoredToken(null)
  }

  /** Password step. Resolves to `'totp'` when a second factor is demanded. */
  async function login(username: string, password: string): Promise<'signed-in' | 'totp'> {
    const result = await api.post<LoginResult>('/auth/login', { username, password })
    if (result.totp_required) {
      totpChallenge.value = result.challenge_token ?? null
      return 'totp'
    }
    if (!result.token || !result.user) {
      // A success envelope without a session is not something a screen can
      // recover from, and pretending otherwise would leave a signed-out user
      // looking at a dashboard shell.
      throw new ApiError({
        code: 'malformed_response',
        message: 'The sign-in succeeded but no session was returned.',
        status: 200,
      })
    }
    setSession(result.token, result.user)
    return 'signed-in'
  }

  /** Second factor. Consumes the challenge token from the password step. */
  async function verifyTotp(code: string): Promise<void> {
    const challenge = totpChallenge.value
    if (challenge === null) {
      throw new ApiError({
        code: 'unauthenticated',
        message: 'The sign-in attempt expired. Enter your username and password again.',
        status: 401,
      })
    }
    const result = await api.post<LoginResult>('/auth/login/totp', {
      challenge_token: challenge,
      code,
    })
    if (!result.token || !result.user) {
      throw new ApiError({
        code: 'malformed_response',
        message: 'The code was accepted but no session was returned.',
        status: 200,
      })
    }
    setSession(result.token, result.user)
  }

  function abandonTotp(): void {
    totpChallenge.value = null
  }

  /**
   * Re-reads the identity behind the stored token.
   *
   * Called on every application start and after a role change. A 401 here is
   * the normal path for an expired token, not an error worth showing.
   */
  async function loadMe(): Promise<boolean> {
    if (token.value === null) {
      ready.value = true
      return false
    }
    resolving.value = true
    try {
      user.value = await api.get<StaffUser>('/auth/me')
      return true
    } catch (error) {
      if (hasCode(error, 'unauthenticated') || hasCode(error, 'forbidden')) {
        clearSession()
        return false
      }
      // A network failure is NOT a signed-out state. Clearing the session here
      // would sign a user out every time a jetty link drops, and they would
      // have to re-authenticate to see a screen they already had open.
      throw error
    } finally {
      resolving.value = false
      ready.value = true
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch {
      // The local session is being discarded regardless. A server that cannot
      // be reached must not strand the user in a signed-in shell.
    } finally {
      clearSession()
    }
  }

  // --- session management (the "where am I signed in" screen) ---------------

  async function listSessions(): Promise<StaffSessionInfo[]> {
    return api.get<StaffSessionInfo[]>('/auth/sessions')
  }

  async function revokeSession(id: string): Promise<void> {
    await api.del(`/auth/sessions/${id}`)
  }

  async function revokeAllSessions(): Promise<number> {
    const result = await api.post<{ revoked: number }>('/auth/sessions/revoke-all')
    return result.revoked
  }

  // --- two-factor enrolment -------------------------------------------------

  async function beginTotpEnrolment(): Promise<TotpEnrolment> {
    return api.post<TotpEnrolment>('/auth/totp/enrol')
  }

  async function confirmTotpEnrolment(code: string): Promise<void> {
    await api.post('/auth/totp/confirm', { code })
    if (user.value) user.value = { ...user.value, totp_enabled: true }
  }

  async function disableTotp(): Promise<void> {
    await api.del('/auth/totp')
    if (user.value) user.value = { ...user.value, totp_enabled: false }
  }

  // The client reads the token through this hook on every request rather than
  // capturing it, so a session replaced mid-flight is never sent stale.
  //
  // There is no refresh endpoint on this surface: staff sessions are opaque
  // and long-lived, and a 401 means the session was revoked or expired. So the
  // handler reports "not repaired" and lets the router's guard route to the
  // sign-in screen. Returning `true` here would retry a request that cannot
  // succeed.
  installCredentials({
    getToken: () => token.value,
    onUnauthorized: () => {
      clearSession()
      return false
    },
  })

  return {
    token,
    user,
    ready,
    resolving,
    totpChallenge,
    signedIn,
    permissions,
    siteScope,
    siteRestricted,
    can,
    canAny,
    login,
    verifyTotp,
    abandonTotp,
    loadMe,
    logout,
    listSessions,
    revokeSession,
    revokeAllSessions,
    beginTotpEnrolment,
    confirmTotpEnrolment,
    disableTotp,
  }
})
